import React, { useState } from 'react';
import LeftArrow from '../assets/Left_arrow.svg';
import useIsMobile from '../hooks/useIsMobile';
import { useConsentActions } from '../hooks/useConsentActions';
import { formatFIType } from '../utils/fiGroups';
import { sortFITypes, sortAccountsByName, groupAccountsBySuperType } from '../utils/sortingUtils';
import './ConsentDetails.css';
import AuthApis from '../helper/api.service';
import Revoke from '../assets/Revoke.svg';
import RevokeConfirmDialog from './RevokeConfirmDialog';

const FiuConsentDetails = (props) => {
    const isMobile = useIsMobile();
    const authApis = new AuthApis();
    const [state, setState] = useState({
        arrowHover: false,
    });
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
    const { revokeConsents, revokeSpinner } = useConsentActions(
        authApis,
        props.updateLoader,
        props.history
    );

    const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1).toLowerCase();

    const handleBack = () => {
        props.history.push({
            pathname: `/revoke-consent`,
            state: {
                selectedAccounts: props?.location?.state?.selectedAccounts,
                number: props?.location?.state?.number,
                fromConsentDetails: true, // Flag to indicate coming back from consent details
            },
        });
    };

    const dateFormat = (dateString, showTime) => {
        if (dateString) {
            let newDate = new Date(dateString);
            let dataArray = newDate.toString().split(' ');
            if (showTime) {
                return `${dataArray[2]} ${dataArray[1]} ${dataArray[3]} ${dataArray[4]}`;
            }
            return `${dataArray[2]} ${dataArray[1]} ${dataArray[3]}`;
        }
    };

    const handleRevoke = () => {
        setShowRevokeConfirm(true);
    };

    const handleConfirmRevoke = () => {
        revokeConsents(
            [props?.location?.state?.consent],
            props?.location?.state?.number,
        );
    };

    const handleCloseRevokeConfirm = () => {
        if (!revokeSpinner) {
            setShowRevokeConfirm(false);
        }
    };

    return (
        <section className="consent-details-page px-3 text-start" style={{ paddingTop: '100px', paddingBottom: isMobile ? '180px' : '3rem' }}>
            <style>{`
                .consent-details-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .consent-details-scroll::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .consent-details-scroll::-webkit-scrollbar-thumb {
                    background: #d0d0d0;
                    border-radius: 10px;
                }
                .consent-details-scroll::-webkit-scrollbar-thumb:hover {
                    background: #b0b0b0;
                }
                /* Firefox */
                .consent-details-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: #d0d0d0 #f1f1f1;
                }
            `}</style>
            <div className="mx-auto" style={{ maxWidth: '1200px' }}>
                <div className="d-flex align-items-center mb-3">
                    <button
                        type="button"
                        className="card-action-btn d-inline-flex align-items-center justify-content-center rounded-circle"
                        onClick={handleBack}
                        aria-label="Go back"
                        onMouseEnter={() =>
                            setState((prev) => ({ ...prev, arrowHover: true }))
                        }
                        onMouseLeave={() =>
                            setState((prev) => ({ ...prev, arrowHover: false }))
                        }
                        style={{
                            width: '32px',
                            height: '32px',
                            background: state.arrowHover ? '#f0f0f0' : 'transparent',
                            transition: 'background 0.2s',
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={LeftArrow}
                            alt=""
                            aria-hidden="true"
                            style={{ height: '32px', width: '32px' }}
                        />
                    </button>
                    <h1 className="fw-semibold text-black m-0" style={{ fontSize: '16px', fontWeight: '600' }}>
                        Consent Details
                    </h1>
                </div>

                <p className="mb-4 text-start" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                    You can revoke this consent given to {props?.location?.state?.consent?.consentDetails?.FIU?.name}
                </p>

                <div className={`d-flex ${isMobile ? 'flex-column' : 'flex-row'}`} style={{
                    gap: '16px', ...(!isMobile && {
                        maxHeight: '36rem',
                    }),
                }}>
                    {/* Left card - Consent Details */}
                    <div 
                        className="bg-white border rounded-3 p-4 px-3 d-flex flex-column"
                        style={{
                            flex: isMobile ? '1' : '0 0 48%',
                            borderColor: '#E0E0E0',
                            maxHeight: isMobile ? 'none' : '36rem',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="consent-details-scroll" style={{ flex: '1', overflowY: 'auto', minHeight: 0 }}>
                            <h2 className="fw-semibold" style={{ color: '#111111', fontSize: '14px',marginBottom: '2px' }}>
                                Consent Purpose
                            </h2>
                            <p className="mb-4" style={{ color: '#666', lineHeight: '1.5', fontSize: '14px' }}>
                                {props?.location?.state?.consent?.consentDetails?.Purpose?.text}
                            </p>

                            {/* Two Column Grid for Details */}
                            <div className="row g-3">
                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Consent given on
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {dateFormat(props?.location?.state?.consent?.consentDetails?.createTime, false)}
                                </p>
                            </div>

                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Consent expires on
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {dateFormat(props?.location?.state?.consent?.consentDetails?.expireTime, false)}
                                </p>
                            </div>

                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Account Types
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {props?.location?.state?.consent?.consentDetails?.fiTypes
                                        ? props.location.state.consent.consentDetails.fiTypes.map(formatFIType).join(', ')
                                        : ""}
                                </p>
                            </div>

                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Data Stored For
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {props?.location?.state?.consent?.consentDetails?.DataLife
                                        ? `${props.location.state.consent.consentDetails.DataLife.value} ${props.location.state.consent.consentDetails.DataLife.unit}`
                                        : ''}
                                </p>
                            </div>

                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Data Start Date
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {dateFormat(props?.location?.state?.consent?.consentDetails?.DataDateTimeRange?.from, false)}
                                </p>
                            </div>

                            <div className="col-6">
                                    <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Data End Date
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {dateFormat(props?.location?.state?.consent?.consentDetails?.DataDateTimeRange?.to, false)}
                                </p>
                            </div>

                            <div className="col-12">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Account Details
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {props?.location?.state?.consent?.consentDetails?.consentTypes
                                        ? props.location.state.consent.consentDetails.consentTypes
                                            .map((el) => capitalize(el).replace(/[-_]/g, ' '))
                                            .join(', ')
                                        : 'Profile, Summary, Transactions'}
                                </p>
                            </div>

                            <div className="col-6">
                                <p className="mb-1" style={{ color: '#6C6C6C', fontSize: '14px' }}>
                                    Purpose Code
                                </p>
                                <p className="fw-medium" style={{ color: '#111111', fontSize: '14px' }}>
                                    {props?.location?.state?.consent?.consentDetails?.Purpose?.code}
                                </p>
                            </div>
                        </div>
                        </div>

                        <div className="mt-3 pt-3 border-top flex-shrink-0" style={{ borderColor: '#E0E0E0', color: '#6C6C6C', fontSize: '14px' }}>
                            For any grievances you can reach out to us at grievances@cookiejar.co.in
                        </div>
                </div>

                {/* Right card - Accounts */}
                <div 
                    className="bg-white border rounded-3 p-4 px-3 d-flex flex-column"
                    style={{
                        flex: isMobile ? '1' : '0 0 48%',
                        borderColor: '#E0E0E0',
                    }}
                >
                    <div className="mb-4">
                        <h2 className="fw-semibold m-0" style={{ color: '#111111', fontSize: '14px',fontWeight: '600' }}>
                            Accounts
                        </h2>
                    </div>

                    {/* Scrollable Accounts List - Grouped by FIType */}
                    <div className="mb-4 consent-details-scroll" style={{ flex: 1, overflowY: 'auto' }}>
                        {(() => {
                            const accounts = props?.location?.state?.consent?.consentDetails?.Accounts || [];
                            
                            // Group accounts by SuperType
                            const accountsBySuperType = groupAccountsBySuperType(accounts);

                            const superTypes = sortFITypes(Object.keys(accountsBySuperType));

                            return superTypes.map((superType, index) => {
                                const superTypeAccounts = sortAccountsByName(accountsBySuperType[superType]);
                                const groupHeadingId = `fiu-accounts-group-${superType.replace(/\s+/g, '-')}-${index}`;

                                return (
                                    <div
                                        key={superType}
                                        className="mb-3"
                                        role="group"
                                        aria-labelledby={groupHeadingId}
                                    >
                                        <h3
                                            id={groupHeadingId}
                                            className="fw-medium mb-2"
                                            style={{ color: '#6C6C6C', fontSize: '14px' }}
                                        >
                                            {superType} Account(s)
                                        </h3>
                                        <ul className="list-unstyled m-0 p-0" role="list">
                                            {superTypeAccounts.map((account, accIndex) => {
                                                const fiTypeLabel = formatFIType(account?.fiType || account?.FIType);
                                                return (
                                                    <li
                                                        key={accIndex}
                                                        role="listitem"
                                                        aria-label={`${account?.fipName}, account ending ${account?.maskedAccNumber?.slice(-4)}, ${fiTypeLabel}`}
                                                        className="d-flex align-items-center mb-2 px-3 py-2"
                                                        style={{
                                                            backgroundColor: '#F8F8F8',
                                                            gap: '12px',
                                                            fontSize: '14px',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <div
                                                            className="bg-white rounded d-flex align-items-center justify-content-center"
                                                            style={{ width: '24px', height: '24px' }}
                                                            aria-hidden="true"
                                                        >
                                                            <img
                                                                src={account.fipLogoUri}
                                                                alt=""
                                                                style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                        <span
                                                            className="fw-medium"
                                                            aria-hidden="true"
                                                            style={{ color: '#111111', fontWeight: '500', fontSize: '12px', lineHeight: '14px' }}
                                                        >
                                                            {account?.fipName} • {account?.maskedAccNumber} • <span style={{ color: '#6C6C6C' }}>{fiTypeLabel}</span>
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            });
                        })()}
                    </div>

                    {/* Revoke Button */}
                    <button
                        type="button"
                        onClick={handleRevoke}
                        disabled={revokeSpinner}
                        aria-busy={revokeSpinner}
                        className="w-100 btn text-white rounded-2 fw-semibold d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: props.customDetails?.primaryColor || '#3E4DAC',
                            padding: '9.5px 14px',
                            gap: '8px',
                            transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.opacity = '1';
                        }}
                    >
                        <img src={Revoke} alt="" aria-hidden="true" style={{ width: '20px', height: '20px' }} />
                        Revoke consent
                    </button>
                </div>
            </div>
            </div>

            <RevokeConfirmDialog
                isOpen={showRevokeConfirm}
                onClose={handleCloseRevokeConfirm}
                onConfirm={handleConfirmRevoke}
                accounts={[props?.location?.state?.consent].filter(Boolean)}
                customDetails={props.customDetails}
                isLoading={revokeSpinner}
                groupByEntity={true}
            />
        </section>
    );
};

export default FiuConsentDetails;
